import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { AuditLog } from '../../types/admin';
import { auditLogApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { formatDateTime } from '../../utils/date';

/** Cố gắng format chuỗi JSON để dễ đọc; nếu không phải JSON hợp lệ, trả nguyên văn. */
function prettyPrint(value: string | null): string {
  if (!value) return '—';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

/** Bảng nhật ký hệ thống (chỉ đọc, append-only) + bộ lọc + xem chi tiết thay đổi. */
export function AuditLogViewer(): JSX.Element {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [detailLog, setDetailLog] = useState<AuditLog | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await auditLogApi.list({
        action: actionFilter.trim() || undefined,
        entity_type: entityTypeFilter.trim() || undefined,
        limit: 200,
      });
      setLogs(list);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải nhật ký hệ thống'), 'error');
    } finally {
      setLoading(false);
    }
  }, [actionFilter, entityTypeFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleFilterSubmit = (e: FormEvent) => {
    e.preventDefault();
    void load();
  };

  const handleClearFilter = () => {
    setActionFilter('');
    setEntityTypeFilter('');
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Nhật ký hệ thống
        </Typography>
        <Tooltip title="Tải lại">
          <IconButton onClick={() => void load()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box component="form" onSubmit={handleFilterSubmit}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              label="Hành động"
              size="small"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              placeholder="VD: CREATE, UPDATE, DELETE"
            />
            <TextField
              label="Loại đối tượng"
              size="small"
              value={entityTypeFilter}
              onChange={(e) => setEntityTypeFilter(e.target.value)}
              placeholder="VD: project, feature, user"
            />
            <Button type="submit" variant="contained">
              Lọc
            </Button>
            <Button type="button" onClick={handleClearFilter}>
              Xóa lọc
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Thời gian</TableCell>
                <TableCell>Người thực hiện</TableCell>
                <TableCell>Hành động</TableCell>
                <TableCell>Loại đối tượng</TableCell>
                <TableCell>Mã đối tượng</TableCell>
                <TableCell align="right">Chi tiết</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Không có nhật ký nào phù hợp.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>{formatDateTime(log.created_at)}</TableCell>
                    <TableCell>
                      {log.actor_user_name ?? (log.actor_user_id ? 'Người dùng đã xoá' : '—')}
                    </TableCell>
                    <TableCell>
                      <Chip label={log.action} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{log.entity_type}</TableCell>
                    <TableCell>{log.entity_id ?? '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Xem chi tiết thay đổi">
                        <IconButton size="small" onClick={() => setDetailLog(log)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={detailLog !== null} onClose={() => setDetailLog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Chi tiết nhật ký</DialogTitle>
        <DialogContent dividers>
          {detailLog && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={4} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Thời gian
                  </Typography>
                  <Typography variant="body2">{formatDateTime(detailLog.created_at)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Hành động
                  </Typography>
                  <Typography variant="body2">{detailLog.action}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Loại đối tượng
                  </Typography>
                  <Typography variant="body2">{detailLog.entity_type}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Mã đối tượng
                  </Typography>
                  <Typography variant="body2">{detailLog.entity_id ?? '—'}</Typography>
                </Box>
              </Stack>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Giá trị cũ
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    bgcolor: 'action.hover',
                    p: 1.5,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: 220,
                    fontSize: 12,
                    m: 0,
                  }}
                >
                  {prettyPrint(detailLog.old_value)}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Giá trị mới
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    bgcolor: 'action.hover',
                    p: 1.5,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: 220,
                    fontSize: 12,
                    m: 0,
                  }}
                >
                  {prettyPrint(detailLog.new_value)}
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailLog(null)}>Đóng</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
